import { useEffect, useId, useRef, type ReactNode } from 'react'
import { FormRenderer } from '@/core/components/form/FormRenderer'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { formatDatum, todayIso } from '@/core/lib/dates'
import {
  BidiText,
  Callout,
  Document,
  DocumentFields,
  DocumentSection,
  DocumentSubtitle,
  DocumentTitle,
  FormSection,
  Icon,
  ItemNumber,
  Panel,
  PanelHead,
  Paragraph,
  Prose,
  SignatureLine,
  StatusLine,
  Title,
  VisuallyHidden,
  type DocumentField,
} from 'admination-react'
import type { PortalBlatt, PortalInhalt } from '../../api/portal'
import {
  blattKey,
  fehlendeAntworten,
  fieldValue,
  sheetFields,
  verbisValue,
  withKorrektur,
  type BlattStatus,
  type Daten,
  type SheetDef,
} from '../../lib/teilnehmerakte'

export const GERMAN_AUTHORITATIVE = 'Maßgeblich ist die deutsche Fassung.'

export function readingInhalt(blatt: Pick<PortalBlatt, 'inhalt' | 'inhalt_de'> | undefined, language: string): PortalInhalt | null {
  const { inhalt, inhalt_de } = blatt ?? {}
  if (inhalt?.sprache === language) return inhalt
  return inhalt_de ?? (inhalt?.sprache === 'de' ? inhalt : null)
}

function paragraphsOf(inhalt: PortalInhalt | null | undefined): string[] {
  if (!inhalt || typeof inhalt.text !== 'string') return []
  return inhalt.text
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter((p) => p !== '')
}

export function InhaltText({ inhalt }: { inhalt: PortalInhalt | null | undefined }) {
  const paragraphs = paragraphsOf(inhalt)
  if (paragraphs.length === 0) return null
  return <Prose paragraphs={paragraphs} lang={inhalt?.sprache || undefined} dir="auto" />
}

export type SheetViewProps = {
  sheet: SheetDef
  status: BlattStatus
  daten: Daten | undefined
  inhalt: PortalInhalt | null | undefined
  focusMissing?: number
  readOnly?: boolean
  onChange: (daten: Daten) => void
}

function focusFirstMissing(root: HTMLElement | null) {
  const marked = root?.querySelector<HTMLElement>('[aria-invalid="true"]')
  const target = marked?.matches('input') ? marked : marked?.querySelector<HTMLElement>('input')
  target?.focus({ preventScroll: true })
  ;(marked ?? target)?.scrollIntoView?.({ block: 'center' })
}

export function SheetView({ sheet, status, daten, inhalt, focusMissing = 0, readOnly = false, onChange }: SheetViewProps) {
  const { t } = useTranslation('portal')
  const frozen = readOnly || status === 'unterschrieben'
  const key = blattKey(sheet.id)
  const headingId = useId()
  const phoneHintId = useId()
  const panelRef = useRef<HTMLElement>(null)
  const missing = frozen ? new Set<string>() : fehlendeAntworten(sheet, daten)

  useEffect(() => {
    if (focusMissing > 0) focusFirstMissing(panelRef.current)
  }, [focusMissing])

  return (
    <Panel ref={panelRef} aria-labelledby={headingId}>
      <PanelHead>
        <ItemNumber dir="ltr">{sheet.id}</ItemNumber>
        <Title as="h2" size="lg" id={headingId} dir="auto" lang={inhalt?.titel?.trim() ? inhalt.sprache : undefined}>
          {inhalt?.titel?.trim() ? inhalt.titel : t(`blatt.${key}`)}
        </Title>
      </PanelHead>

      {sheet.kind === 'review' && !frozen ? <Paragraph variant="lead">{t('review.intro')}</Paragraph> : null}
      {sheet.kind === 'fill' && !frozen ? <Paragraph variant="lead">{t('fill.intro')}</Paragraph> : null}
      {sheet.kind === 'sign' && !frozen ? <Paragraph variant="lead">{t('sign.formIntro')}</Paragraph> : null}

      <InhaltText inhalt={inhalt} />

      <FormRenderer
          definition={sheet}
          values={daten ?? {}}
          onChange={(id, value) =>
            onChange(sheet.kind === 'review' ? withKorrektur(daten, id, String(value)) : { ...(daten ?? {}), [id]: value })
          }
          section={(id) => ({
            heading: sheet.sections[id].heading ? t(sheet.sections[id].heading) : '',
            after: sheet.kind === 'fill' ? { text: t('fill.phoneHint'), id: phoneHintId } : undefined,
          })}
          field={(field) => {
            const absent = missing.has(field.id)
            const common = { disabled: frozen, requiredLabel: t('required') }
            if (field.control === 'matrix') {
              const invalid = field.rows.some((row) => missing.has(`${field.id}.${row}`))
              return {
                ...common,
                label: t('devices.title'),
                invalid,
                errorMessage: invalid ? t('required') : undefined,
                rowInvalid: missing,
                optionLabel: (option) => t(`devices.${option}`),
                rowLabel: (row) => t(`devices.${row}`),
              }
            }
            if (field.control === 'declaration')
              return { ...common, label: t(`verbis.${field.label}`), invalid: absent, errorMessage: absent ? t('required') : undefined }
            if (sheet.kind === 'sign')
              return {
                ...common,
                label: t('consent.question'),
                invalid: absent,
                errorMessage: absent ? t('required') : undefined,
                optionLabel: (option) => t(option === 'einverstanden' ? 'consent.einverstanden' : 'consent.nichtEinverstanden'),
              }
            const value = fieldValue(sheet, daten, field.id)
            const phone = sheet.kind === 'fill' && sheet.anyOf.includes(field.id)
            const filled = value.trim() !== ''
            const prefilled = sheet.kind === 'review' && filled && value === verbisValue(daten, field.id)
            return {
              ...common,
              label: t(`field.${field.label}`),
              value: field.control === 'choice' ? value.toLowerCase() : value,
              invalid: absent,
              errorMessage: absent && !phone ? t('required') : undefined,
              descriptionId: absent && phone ? phoneHintId : undefined,
              size: 'lg',
              dir: 'auto',
              autoComplete: 'off',
              placeholder: sheet.kind === 'review' && !frozen ? t('review.fillIn') : undefined,
              optionLabel: (option) => t(`field.${field.label}${option.charAt(0).toUpperCase()}${option.slice(1)}`),
              markDescribed: prefilled,
              mark: filled ? (
                <>
                  <Icon name="i-check" />
                  {prefilled ? <VisuallyHidden>{t('review.prefilled')}</VisuallyHidden> : null}
                </>
              ) : null,
            }
          }}
        />
    </Panel>
  )
}

function answersOf(sheet: SheetDef, daten: Daten | undefined, t: TFunction): DocumentField[] {
  if (sheet.kind !== 'sign') return []
  return sheetFields(sheet).flatMap((field) => {
    if (field.control === 'choice') {
      const choice = daten?.[field.id]
      return [
        {
          term: t('consent.question'),
          value:
            choice === 'einverstanden'
              ? t('consent.einverstanden')
              : choice === 'nicht_einverstanden'
                ? t('consent.nichtEinverstanden')
                : '',
        },
      ]
    }
    if (field.control === 'matrix') {
      const answers = daten?.[field.id] && typeof daten[field.id] === 'object' ? (daten[field.id] as Record<string, unknown>) : {}
      return field.rows.map((row) => ({
        term: t(`devices.${row}`),
        value: answers[row] === 'ja' ? t('devices.ja') : answers[row] === 'nein' ? t('devices.nein') : '',
      }))
    }
    return [{ term: t(`verbis.${field.label}`), value: daten?.[field.id] === true ? t('devices.ja') : t('devices.nein') }]
  })
}

export function sheetAnswers(sheet: SheetDef, daten: Daten | undefined, t: TFunction): DocumentField[] {
  if (sheet.kind === 'sign') return answersOf(sheet, daten, t)
  return sheetFields(sheet).map((field) => {
    const value = fieldValue(sheet, daten, field.id)
    const shown =
      field.control === 'choice' && value !== '' ? t(`field.${field.label}${value.charAt(0).toUpperCase()}${value.slice(1)}`) : value
    return { term: t(`field.${field.label}`), value: shown === '' ? '\u2014' : shown }
  })
}

function DocumentPage({
  inhalt,
  fallbackTitel,
  subtitle,
  language,
  answers,
  answersLabel,
  children,
}: {
  inhalt: PortalInhalt | null | undefined
  fallbackTitel: string
  subtitle: string
  language: string
  answers: DocumentField[]
  answersLabel: string
  children?: React.ReactNode
}) {
  const paragraphs = paragraphsOf(inhalt)
  return (
    <Document lang={language} dir="auto">
      <DocumentTitle>{inhalt?.titel?.trim() ? inhalt.titel : fallbackTitel}</DocumentTitle>
      <DocumentSubtitle>{subtitle}</DocumentSubtitle>
      {paragraphs.map((paragraph, i) => (
        <p key={i}>
          <BidiText text={paragraph} />
        </p>
      ))}
      {answers.length > 0 ? (
        <>
          <DocumentSection>{answersLabel}</DocumentSection>
          <DocumentFields fields={answers} />
        </>
      ) : null}
      {children}
    </Document>
  )
}

export type SheetDocumentProps = {
  sheet: SheetDef
  status: BlattStatus
  daten: Daten | undefined
  inhalt: PortalInhalt | null | undefined
  inhaltDe: PortalInhalt | null | undefined
  language: string
  unterschriebenAm: string | null | undefined
  signatureSrc?: string
  signerName: string
  bindingPdf?: ReactNode
}

export function SheetDocument({
  sheet,
  status,
  daten,
  inhalt,
  inhaltDe,
  language,
  unterschriebenAm,
  signatureSrc,
  signerName,
  bindingPdf,
}: SheetDocumentProps) {
  const { t, i18n } = useTranslation('portal')
  const de = i18n.getFixedT('de', 'portal')
  const signed = status === 'unterschrieben'
  const key = blattKey(sheet.id)
  const translated = language !== 'de'
  const translation = translated && inhalt?.sprache === language ? inhalt : null

  return (
    <>
      {translation ? (
        <>
          <Callout note title={GERMAN_AUTHORITATIVE} titleLang="de" titleDir="ltr">
            {t('legal.translated')}
          </Callout>
          <DocumentPage
            inhalt={translation}
            fallbackTitel={t(`blatt.${key}`)}
            subtitle={t('document.translationSubtitle', { blatt: sheet.id })}
            language={language}
            answers={answersOf(sheet, daten, t)}
            answersLabel={t('document.answers')}
          />
        </>
      ) : null}
      {bindingPdf ?? (
        <DocumentPage
          inhalt={inhaltDe ?? (inhalt?.sprache === 'de' ? inhalt : null)}
          fallbackTitel={de(`blatt.${key}`)}
          subtitle={`Blatt ${sheet.id} · Maßgebliche Fassung`}
          language="de"
          answers={answersOf(sheet, daten, de)}
          answersLabel={de('document.answers')}
        >
          <DocumentSection>Unterschrift</DocumentSection>
          <SignatureLine
            placeDate={formatDatum(signed ? unterschriebenAm : todayIso())}
            name={signerName}
            signatureSrc={signatureSrc}
            missingLabel={signed ? 'unterschrieben' : 'noch nicht unterschrieben'}
          />
        </DocumentPage>
      )}
      {signed ? (
        <FormSection compact>
          <StatusLine tone="success" strong icon={<Icon name="i-check" />}>
            {unterschriebenAm ? t('sign.signedAt', { datum: formatDatum(unterschriebenAm) }) : t('sign.signed')}
          </StatusLine>
          <Paragraph variant="muted">{t('sign.frozen')}</Paragraph>
        </FormSection>
      ) : null}
    </>
  )
}
